// Admin-Multi-Survey-Verwaltung — alles im Browser, kein Server-Linking.
// Slot speichert beim Anlegen jeder Survey eine Mini-Referenz im localStorage,
// damit der Admin auf /admin (ohne Token) eine Liste seiner Surveys sieht.
// Privacy-First: der Server weiss nichts davon, keine Cross-Survey-Verknüpfung.

const KEY = 'slot:admin-surveys';

export type AdminSurveyRef = {
  id: string;
  title: string | null;
  adminToken: string;
  voterToken: string;
  createdAt: number;       // unix seconds
  expiresAt: number | null;
  /** Falls vom User wegverbannt: nicht mehr im Dashboard zeigen. */
  hidden?: boolean;
};

export function listSurveys(): AdminSurveyRef[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    return data.filter(isValidRef);
  } catch {
    return [];
  }
}

export function addSurvey(ref: AdminSurveyRef): void {
  const all = listSurveys();
  // Doppel-Adds verhindern (z. B. wenn Master-File mehrfach importiert wird).
  if (all.some((s) => s.adminToken === ref.adminToken)) return;
  all.push(ref);
  save(all);
}

export function removeSurvey(adminToken: string): void {
  const all = listSurveys().filter((s) => s.adminToken !== adminToken);
  save(all);
}

/** Mehrere Refs auf einmal mergen (für Master-File-Import). Idempotent. */
export function mergeSurveys(refs: AdminSurveyRef[]): { added: number; existing: number } {
  const all = listSurveys();
  const known = new Set(all.map((s) => s.adminToken));
  let added = 0, existing = 0;
  for (const r of refs) {
    if (!isValidRef(r)) continue;
    if (known.has(r.adminToken)) {
      existing++;
      continue;
    }
    all.push(r);
    known.add(r.adminToken);
    added++;
  }
  save(all);
  return { added, existing };
}

function save(all: AdminSurveyRef[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // localStorage voll oder deaktiviert — schweigend ignorieren.
  }
}

function isValidRef(x: unknown): x is AdminSurveyRef {
  if (typeof x !== 'object' || x === null) return false;
  const r = x as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.adminToken === 'string' &&
    /^[a-f0-9]{32}$/.test(r.adminToken) &&
    typeof r.voterToken === 'string' &&
    /^[a-f0-9]{32}$/.test(r.voterToken) &&
    (r.title === null || typeof r.title === 'string') &&
    typeof r.createdAt === 'number' &&
    (r.expiresAt === null || typeof r.expiresAt === 'number')
  );
}
